import { HttpResult, HttpPagedResult } from '../models/HttpResult';
import { FileUpload, FileUploadRequest } from '../models/Entities/Contracts/FileUpload';
import { FileUploadFilters } from '../models/Filters/FileUploadFilters';

// Add polyfill for AbortSignal.timeout() if it doesn't exist
// This is needed for TypeScript compatibility as the method may be missing from type definitions
declare global {
    interface AbortSignalConstructor {
        timeout?: (ms: number) => AbortSignal;
    }
}

// Polyfill implementation for AbortSignal.timeout
function createTimeoutSignal(ms: number): AbortSignal {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), ms);
    return controller.signal;
}

export class FileUploadServices {
    
    private static readonly baseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'contracts';
    private static readonly TIMEOUT_5MIN = 300000; // 5 minutes
    private static readonly TIMEOUT_10MIN = 600000; // 10 minutes
    private static readonly TIMEOUT_20MIN = 1200000; // 20 minutes
    private static readonly TIMEOUT_30MIN = 1800000; // 30 minutes

    private static getUploadTimeoutMs(fileSize?: number): number {
        // Allow overriding timeout via env for constrained networks.
        const envTimeout = Number(process.env.REACT_APP_FILE_UPLOAD_TIMEOUT_MS);
        if (Number.isFinite(envTimeout) && envTimeout > 0) {
            return envTimeout;
        }

        if (!fileSize) {
            return this.TIMEOUT_10MIN;
        }

        // Dynamic timeout by file size to reduce false timeouts on large uploads.
        if (fileSize <= 25 * 1024 * 1024) {
            return this.TIMEOUT_5MIN;
        }
        if (fileSize <= 100 * 1024 * 1024) {
            return this.TIMEOUT_10MIN;
        }
        if (fileSize <= 500 * 1024 * 1024) {
            return this.TIMEOUT_20MIN;
        }
        return this.TIMEOUT_30MIN;
    }

    /**
     * Handles a single file upload with metadata
     * @param fileRequest - The file request containing the file and metadata
     * @param token - Authorization token
     * @returns Promise with the uploaded file data
     */
    static async uploadFile(
        fileRequest: FileUploadRequest,
        token: string
    ): Promise<HttpResult<FileUpload>> {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('files', fileRequest.file);
            if (fileRequest.entityType) {
                formData.append('entityType', fileRequest.entityType);
            }
            if (fileRequest.entityId !== undefined) {
                formData.append('entityId', fileRequest.entityId.toString());
            }
            if (fileRequest.tags && fileRequest.tags.length > 0) {
                fileRequest.tags.forEach((tag, index) => {
                    formData.append(`tags[${index}]`, tag);
                });
            }
            if (fileRequest.metadata) {
                formData.append('metadata', JSON.stringify(fileRequest.metadata));
            }

            const xhr = new XMLHttpRequest();

            if (fileRequest.onUploadProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && fileRequest.onUploadProgress) {
                        fileRequest.onUploadProgress(Math.round((event.loaded / event.total) * 100));
                    }
                };
            }

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const data = JSON.parse(xhr.responseText) as FileUpload;
                        resolve({
                            data,
                            succeeded: true,
                            message: 'File uploaded successfully',
                            statusCode: xhr.status,
                            errors: []
                        });
                    } catch {
                        resolve({
                            data: null as any,
                            succeeded: true,
                            message: 'File uploaded successfully',
                            statusCode: xhr.status,
                            errors: []
                        });
                    }
                } else {
                    let backendMessage = `Upload failed (${xhr.status})`;
                    let backendCode = xhr.status;

                    try {
                        const responseBody = JSON.parse(xhr.responseText ?? '{}');
                        backendMessage =
                            responseBody?.message ||
                            responseBody?.ErrorMessage ||
                            responseBody?.detail ||
                            responseBody?.title ||
                            backendMessage;
                    } catch {
                        if (xhr.responseText) {
                            backendMessage = xhr.responseText;
                        }
                    }

                    const normalized = backendMessage.toLowerCase();
                    const looksLikePayloadTooLarge =
                        normalized.includes('too large') ||
                        normalized.includes('payload') ||
                        normalized.includes('request body') ||
                        normalized.includes('content length') ||
                        normalized.includes('max request') ||
                        normalized.includes('maximum request');

                    if (xhr.status === 413 || (xhr.status === 500 && looksLikePayloadTooLarge)) {
                        backendCode = 413;
                        backendMessage = 'El archivo excede el tamaño máximo permitido por el servidor.';
                    }

                    reject(new Error(JSON.stringify({
                        ErrorCode: backendCode,
                        ErrorMessage: backendMessage
                    })));
                }
            };

            xhr.onerror = () => reject(new Error(JSON.stringify({
                ErrorCode: 0,
                ErrorMessage: 'Network error during upload'
            })));

            xhr.ontimeout = () => reject(new Error(JSON.stringify({
                ErrorCode: 408,
                ErrorMessage: 'Upload timed out while sending file'
            })));

            const url = `${this.baseURL}/${fileRequest.entityId}/files`;
            xhr.open('POST', url);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.timeout = this.getUploadTimeoutMs(fileRequest.file?.size);
            xhr.send(formData);
        });
    }
    
    /**
     * Handles multiple file uploads with metadata
     * @param fileRequests - Array of file requests containing files and metadata
     * @param token - Authorization token
     * @returns Promise with the uploaded files data
     */
    static async uploadMultipleFiles(
        fileRequests: FileUploadRequest[],
        token: string
    ): Promise<HttpResult<FileUpload[]>> {
        return await this.executeContract({
            method: 'POST',
            endpoint: `${this.baseURL}/upload-multiple`,
            token,
            bodyBuilder: () => {
                const formData = new FormData();
                
                fileRequests.forEach((fileRequest, index) => {
                    formData.append(`files`, fileRequest.file);
                    if (fileRequest.entityType) {
                        formData.append(`entityTypes[${index}]`, fileRequest.entityType);
                    }
                    if (fileRequest.entityId) {
                        formData.append(`entityIds[${index}]`, fileRequest.entityId.toString());
                    }
                });
                
                return formData;
            },            responseBuilder: (data: FileUpload[] | null) => ({
                data: data as FileUpload[],
                succeeded: true,
                message: `${data?.length ?? 0} files uploaded successfully`,
                statusCode: 200,
                errors: []
            }),
            errorMessageBuilder: (status) => `Multiple upload failed (${status})`,
            timeoutErrorMessage: 'Multiple upload timeout',            options: {
                signal: createTimeoutSignal(this.TIMEOUT_10MIN),
                skipContentType: true // Let the browser set the Content-Type with boundary
            }
    });
    }
    
    /**
     * Retrieves files with optional filtering and pagination
     * @param filters - Filter and pagination parameters
     * @param token - Authorization token
     * @returns Promise with paged result containing files
     */    
    static async getFiles(
        filters: FileUploadFilters,
        token: string
    ): Promise<HttpPagedResult<FileUpload>> {
        console.log('FileUpload.Services - getFiles called with filters:', filters);
        return await this.executeContract({
            method: 'GET',
            endpoint: `${this.baseURL}/${filters.entityId}/files`,
            token,
            queryParams: undefined,            
                responseBuilder: (responseData: any | null) => {
                console.log('FileUpload.Services - Response data received:', responseData);
                
                // Adaptar la respuesta según su estructura
                let items: FileUpload[] = [];
                let totalCount = 0;
                
                if (responseData) {
                    // Caso 1: { data: FileUpload[], totalCount: number }
                    if (responseData.data && Array.isArray(responseData.data)) {
                        items = responseData.data;
                        totalCount = responseData.totalCount || responseData.data.length;
                    }
                    // Caso 2: Array de FileUpload directo
                    else if (Array.isArray(responseData)) {
                        items = responseData;
                        totalCount = responseData.length;
                    }
                    // Caso 3: { items: FileUpload[], totalCount: number }
                    else if (responseData.items && Array.isArray(responseData.items)) {
                        items = responseData.items;
                        totalCount = responseData.totalCount || responseData.items.length;
                    }
                    // Caso 4: Objeto simple con FileUpload
                    else if (typeof responseData === 'object') {
                        // Intentar adaptarlo si tiene propiedades de un FileUpload
                        if ('id' in responseData && 'fileName' in responseData) {
                            items = [responseData];
                            totalCount = 1;
                        }
                    }
                }
                
                console.log('FileUpload.Services - Processed items:', items);
                console.log('FileUpload.Services - Total count:', totalCount);
                
                return {
                    data: {
                        items: items,
                        totalCount: totalCount,
                        pageIndex: filters.page ?? 0,
                        pageSize: filters.pageSize ?? 20
                    },
                    succeeded: true,
                    message: 'Files retrieved successfully',
                    statusCode: 200,
                    errors: []
                };
            },
            errorMessageBuilder: (status) => `Failed to fetch files (${status})`
        });
    }
    
    /**
     * Deletes a file by ID
     * @param fileId - ID of the file to delete
     * @param token - Authorization token
     * @returns Promise with boolean indicating success
     */
    static async deleteFile(
        fileId: number,
        token: string
    ): Promise<HttpResult<boolean>> {
        return await this.executeContract({
            method: 'DELETE',
            endpoint: `${this.baseURL}/files/${fileId}`,
            token,
            responseBuilder: () => ({
                data: true,
                succeeded: true,
                message: 'File deleted successfully',
                statusCode: 200,
                errors: []
            }),
            errorMessageBuilder: (status) => `Failed to delete file (${status})`
        });
    }    /**
     * Downloads a file by ID
     * @param fileId - ID of the file to download
     * @param token - Authorization token
     * @returns Promise with file blob
     */
    static async downloadFile(
        fileId: number,
        token: string
    ): Promise<{file:Blob, fileName: string}> {
        const headers = {
            'Authorization': `Bearer ${token}`
        };
        
        try {
            const response = await fetch(`${this.baseURL}/files/${fileId}/download`, {
                method: 'GET',
                headers,
                signal: createTimeoutSignal(this.TIMEOUT_5MIN) // Use timeout for downloads too
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(JSON.stringify({
                    ErrorCode: response.status,
                    ErrorMessage: errorData.message ?? `Failed to download file (${response.status})`
                }));
            }

            const blob = await response.blob();
            // Try to extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = 'downloaded-file';
            if (contentDisposition) {
                const filenameRegex = /filename="?([^"]+)"?/;
                const match = filenameRegex.exec(contentDisposition);
                if (match?.[1]) {
                    fileName = match[1];
                }
            }
            return { file: blob, fileName };
        } catch (error: unknown) {
            const err = error as { name?: string; message?: string };
            if (err.name === 'TimeoutError') {
                throw new Error(JSON.stringify({
                    ErrorCode: 408,
                    ErrorMessage: 'Download timeout'
                }));
            }
            
            if (err.message?.startsWith('{')) {
                throw error;
            }
            
            throw new Error(JSON.stringify({
                ErrorCode: 0,
                ErrorMessage: err.message ?? 'Failed to download file'
            }));
        }
    }

    /**
     * Generic contract execution method to handle API requests
     * @param config - Contract execution configuration     * @returns Promise with the result of the contract execution
     */
    private static async executeContract<T, R>(config: {
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        endpoint: string,
        token: string,
        bodyBuilder?: () => FormData | object,
        queryParams?: object,
        responseBuilder: (data: T | null) => R,
        errorMessageBuilder: (status: number) => string,
        timeoutErrorMessage?: string,
        options?: {
            signal?: AbortSignal,
            skipContentType?: boolean
        }
    }): Promise<R> {
        const headers: Record<string, string> = {
            'Authorization': `Bearer ${config.token}`
        };
          // For FormData, do not set Content-Type manually - browser will set it with proper boundary
        if (!config.options?.skipContentType && config.method !== 'POST') {
            headers['Content-Type'] = 'application/json';
        }

        let url = config.endpoint;

        if (config.queryParams) {
            const queryParams = new URLSearchParams();
            Object.entries(config.queryParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    queryParams.append(key, value.toString());
                }
            });
            
            if (queryParams.toString()) {
                url = `${url}?${queryParams.toString()}`;
            }
        }
        
        const fetchOptions: RequestInit = {
            method: config.method,
            headers,
            ...(config.options || {})
        };        if (config.bodyBuilder) {
            fetchOptions.body = config.bodyBuilder() as BodyInit;
        }
        
        try {
            const response = await fetch(url, fetchOptions);            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(JSON.stringify({
                    ErrorCode: response.status,
                    ErrorMessage: errorData.message ?? config.errorMessageBuilder(response.status)
                }));
            }
            
            if (config.method === 'DELETE') {
                // For DELETE operations that don't return content
                return config.responseBuilder(null as any);
            }

            const data = await response.json();
            return config.responseBuilder(data);
        } catch (error: unknown) {
            const err = error as { name?: string; message?: string };
            if (err.name === 'TimeoutError') {
                throw new Error(JSON.stringify({
                    ErrorCode: 408,
                    ErrorMessage: config.timeoutErrorMessage ?? 'Request timed out'
                }));
            }
            
            if (err.message?.startsWith('{')) {
                throw error;
            }
            
            throw new Error(JSON.stringify({
                ErrorCode: 0,
                ErrorMessage: err.message ?? 'Unknown error occurred'
            }));
        }
    }
}
